/**
 * 声音节点：上传音频文件，连线到生视频节点作为参考音频。
 *
 * 一个声音节点 = 一段音频（与图片节点一张图、视频节点一段视频一致）。
 * 上传走 /videos/upload-audio 接口，返回 storage key，
 * 后端 resolveAudioReferenceForUpstream 解析为上游可访问的 ticket URL。
 */
import { useRef, useState } from 'react';
import { Upload, X, Loader2, Music, RefreshCw } from 'lucide-react';
import type { CanvasNode, AudioNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton, NodeToolbarDivider } from './NodeToolbar';
import { boardActions } from '../state/boardStore';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { platformFetch, readEnvelope } from '../../../platform/http';
import { readMediaDuration, formatSeconds } from '../../../baselines/video/utils/readMediaDuration';

interface AudioNodeProps {
  node: CanvasNode<AudioNodeData>;
  selected: boolean;
  highlighted: boolean;
}

const MAX_SIZE_MB = 15;
const ACCEPT = '.mp3,.wav,.m4a,.ogg,.aac,.flac';

export function AudioNode({ node, selected, highlighted }: AudioNodeProps) {
  const data = node.data;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const audioUrl = data.audioStorageKey ? resolveBrowserMediaUrl(data.audioStorageKey) : null;

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`文件大小超过 ${MAX_SIZE_MB}MB 限制`);
      return;
    }

    setError('');
    setUploading(true);
    boardActions.updateNodeData(node.id, { uploading: true });

    // 浏览器端读取时长（失败不阻断上传，仅展示缺失）
    let durationSec: number | null = null;
    try {
      durationSec = await readMediaDuration(file, 'audio');
    } catch {
      // 时长读取失败不阻断上传流程
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await readEnvelope<{ storageKey: string }>(
        await platformFetch('/videos/upload-audio?persist=1', {
          method: 'POST',
          body: formData,
        }),
        '上传失败'
      );

      boardActions.updateNodeData(node.id, {
        audioStorageKey: result.storageKey,
        fileName: file.name,
        durationSec,
        fileSize: file.size,
        mimeType: file.type,
        uploading: false,
      });
    } catch (err: any) {
      boardActions.updateNodeData(node.id, { uploading: false });
      setError(err?.message || '上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    boardActions.updateNodeData(node.id, {
      audioStorageKey: null,
      fileName: null,
      durationSec: null,
      fileSize: null,
      mimeType: null,
    });
    setError('');
  };

  const isEmpty = !audioUrl && !uploading && !data.uploading;

  const toolbar = audioUrl ? (
    <>
      <NodeToolbarButton onClick={() => replaceInputRef.current?.click()} title="替换音频">
        <RefreshCw className="h-3.5 w-3.5" />
      </NodeToolbarButton>
      <NodeToolbarDivider />
      <NodeToolbarButton onClick={handleRemove} title="清空">
        <X className="h-3.5 w-3.5" />
      </NodeToolbarButton>
    </>
  ) : null;

  return (
    <NodeWrapper node={node} selected={selected} highlighted={highlighted} toolbar={toolbar}>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-3">
        {isEmpty ? (
          /* 空态：上传入口 */
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 px-6 py-5 text-slate-400 transition hover:border-pink-400 hover:text-pink-500"
          >
            <Upload className="h-6 w-6" />
            <span className="text-xs">上传音频</span>
          </button>
        ) : uploading || data.uploading ? (
          /* 上传中 */
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs">上传中...</span>
          </div>
        ) : audioUrl ? (
          /* 有音频：播放器 + 信息 */
          <div className="flex w-full flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Music className="h-4 w-4 flex-shrink-0 text-pink-500" />
              <span className="flex-1 truncate text-xs text-slate-600" title={data.fileName ?? undefined}>
                {data.fileName || '音频文件'}
              </span>
              {data.durationSec != null && (
                <span className="flex-shrink-0 text-[10px] text-slate-400">
                  {formatSeconds(data.durationSec)}s
                </span>
              )}
            </div>
            <audio
              controls
              src={audioUrl}
              className="h-8 w-full"
              style={{ minHeight: 32 }}
            />
          </div>
        ) : null}

        {error && <p className="text-[10px] text-rose-500">{error}</p>}
        {!audioUrl && !uploading && !data.uploading && !error && (
          <p className="text-center text-[9px] text-slate-300">
            支持 mp3/wav/m4a 等，≤{MAX_SIZE_MB}MB
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPT}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
    </NodeWrapper>
  );
}
